import React, { useEffect, useRef, useState } from 'react'
import { Translates } from '@/services/repository/i18n'
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from 'react-hook-form'
import { FieldProps, withFieldWrap } from './FormBase'
import { Dict } from '@/services/repository/dict'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { MachineType } from '@/services/model/machine'
import { QRScanner } from '@/components/qr/scanner'
import { NFCReaderPopup } from '@/components/nfc/reader'
import { SimpleMachine } from '@/pages/dashboard/superadmin/machines/Machines'
import { MachineFailureTypes } from '@/services/repository/machines'
import Select from './CustomDropdown'
import CustomDropdown from './CustomDropdown'
import { useModal } from '@/components/modal/Modal'
import { CurrentSession } from '@/services/repository/session'

import './fields.css'

export const idInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={true}
    {...form.register(fieldName as Path<T>)} />

export const idField = <T extends FieldValues>() => {
  const content = withFieldWrap<T>(idInput<T>)
  // Watch the value of 'someField'
  return (props: FieldProps<T>) => {
    const value = useWatch({
      control: props.form.control,
      name: props.fieldName as Path<T>,
    })
    return value ? content(props) : null
  }
}

export const nameInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    })}
  />

export const nameField = <T extends FieldValues>() => withFieldWrap<T>(nameInput<T>)

export const requiredTextInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    })}
  />

export const requiredTextField = <T extends FieldValues>() => withFieldWrap<T>(requiredTextInput<T>)

export const textInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      // required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    })}
  />

export const textField = <T extends FieldValues>() => withFieldWrap<T>(textInput<T>)

export const phoneNumberInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    })}
  />

export const phoneNumberField = <T extends FieldValues>() => withFieldWrap<T>(phoneNumberInput<T>)

export const numberInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='number'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      // setValueAs: (value) => value !== '' ? Number(value) : value,
      validate: (value) => {
        // if (value === '') {
        //   return translates.for(`forms.${formName}.errors.fields.${fieldName}.required`);
        // }
        if (!/^\d*$/.test(value)) {
          return translates.for(`forms.${formName}.errors.fields.${fieldName}.invalid`);
        }
        return true;
      }
    })}
  />

export const numberField = <T extends FieldValues>() => withFieldWrap<T>(numberInput<T>)

export const usernameInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='text'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    })}
  />

export const usernameField = <T extends FieldValues>() => withFieldWrap<T>(usernameInput<T>)

export const langInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => <div className='custom-select'>
    {/* <select
      id={fieldName}
      disabled={readOnly}
      {...form.register(fieldName as Path<T>, {
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      })}>
      {services.i18n.languages().map((lang) => <option value={lang} selected={form.getValues()[fieldName] === lang}>{translates.for('dicts.langs.' + lang)}</option>)}
    </select> */}

    <CustomDropdown
      name={fieldName}
      control={form.control}
      // disabled={readOnly}
      options={services.i18n.languages().map((lang) => ({
        value: lang,
        label: translates.for('dicts.langs.' + lang)
      })) || []}
      rules={{
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      }}
    />


  </div>

export const langField = <T extends FieldValues>() => withFieldWrap<T>(langInput<T>)

export const roleInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => <div className='custom-select'>
    {/* <select
      id={fieldName}
      disabled={readOnly}
      {...form.register(fieldName as Path<T>, {
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      })}>
      {services.users.roles().map((role) => <option value={role} selected={form.getValues()[fieldName] === role}>{translates.for('dicts.roles.' + role)}</option>)}
    </select> */}

    <CustomDropdown
      name={fieldName}
      control={form.control}
      options={services.users.roles().map((role) => ({
        value: role,
        label: translates.for('dicts.roles.' + role)
      })) || []}
      rules={{
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`)
      }}
    />
  </div>

export const roleField = <T extends FieldValues>() => withFieldWrap<T>(roleInput<T>)

export const emailInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='email'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        message: translates.for(`forms.${formName}.errors.fields.${fieldName}.invalid`),
      },
    })} />

export const emailField = <T extends FieldValues>() => withFieldWrap<T>(emailInput<T>)

export const passwordInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly
}: FieldProps<T>) => <input
    type='password'
    id={fieldName}
    readOnly={readOnly}
    placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    {...form.register(fieldName as Path<T>, {
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`)
    })} />

export const passwordField = <T extends FieldValues>() => withFieldWrap<T>(passwordInput<T>)

export const companyIDInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => {
  const [dict, setDict] = useState<Dict[]>()

  useEffect(() => {
    services.session.fetchWithToken(`/api/superadmin/companies/dict`).then((response) => {
      return response.json().then((dict) => setDict(dict))
    })
  }, [])

  if (dict === undefined) {
    return <div className='spinner'></div>
  }

  return <div className='custom-select'>
    <CustomDropdown
      name={fieldName}
      control={form.control}
      options={dict?.map((row) => ({
        value: row.id,
        label: row.label
      })) || []}
      rules={{
        // required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      }}
    />
  </div>
}

export const companyIDField = <T extends FieldValues>() => withFieldWrap<T>(companyIDInput<T>)

export interface machineIDProps<T extends FieldValues> extends FieldProps<T> {
  companyID: string
}

export const machineIDInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => {
  const [machines, setMachines] = useState<SimpleMachine[]>()

  useEffect(() => {
    fetch("/api/schema/machines").then((resp) => {
      return resp.json()
    }).then((data) => {
      setMachines(data)
    });
  }, [])

  if (machines === undefined) {
    return <div className='spinner'></div>
  }

  return <CustomDropdown
    name={fieldName}
    control={form.control}
    options={machines?.map((row) => ({
      value: row.id,
      label: row.name
    })) || []}
    rules={{
      required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
    }}
  />

  // return <div className='custom-select'>
  //   <select
  //     id={fieldName}
  //     disabled={readOnly}
  //     {...form.register(fieldName as Path<T>, {
  //       required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
  //     })}>
  //     {machines?.map((row) => <option value={row.id} selected={form.getValues()[fieldName] === row.id}>{row.name}</option>)}
  //   </select>
  // </div>
}

export const machineIDField = <T extends FieldValues>() => withFieldWrap<T>(machineIDInput<T>)

// Helper function to validate IMEI using Luhn Algorithm
const isValidIMEI = (imei: string): boolean => {
  if(imei==='') {
    return true
  }
  if (!/^[0-9]{15}$/.test(imei)) return false; // Check if it matches the basic pattern
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let digit = parseInt(imei[i]);
    if (i % 2 === 1) { // Double every second digit (0-based index)
      digit *= 2;
      if (digit > 9) digit -= 9; // Subtract 9 if the doubled value is greater than 9
    }
    sum += digit;
  }
  return sum % 10 === 0; // Valid if the sum modulo 10 is 0
};

export const imeiInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly,
  services,
}: FieldProps<T>) => {
  const { openModal } = useModal()

  function modalBody({ onClose }: { onClose: () => void }) {
    const [qrData, setQRData] = useState<string>()
    return <>
      <QRScanner onScan={(data) => {
        setQRData(data)
        if (isValidIMEI(data)) {
          onClose()
          form.setValue(fieldName as any, data as any)
        }
      }} services={services}></QRScanner>
      {qrData
        ? isValidIMEI(qrData)
          ? qrData
          : <div className='error-valid-box'>
            <div>{translates.for(`forms.${formName}.errors.fields.${fieldName}.luhn`)}</div>
            <div>{qrData}</div>
          </div>
        : null}
    </>
  }

  return <div className='imei-fild'>
    <input
      type='text'
      id={fieldName}
      readOnly={readOnly}
      placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
      {...form.register(fieldName as Path<T>, {
        // required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
        validate: {
          // validFormat: (value) => /^[0-9]{15}$/.test(value) || translates.for(`forms.${formName}.errors.fields.${fieldName}.invalid`),
          validIMEI: (value) => isValidIMEI(value) || translates.for(`forms.${formName}.errors.fields.${fieldName}.luhn`),
        },
      })} />
    <div className='toolbox'>
      <div onClick={() => openModal((props) => modalBody(props))} className='icon-button'>
        <i className='svgfont-qrcode' />
      </div>
    </div>
  </div>
}

export const imeiField = <T extends FieldValues>() => withFieldWrap<T>(imeiInput<T>)

export const codeTextInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly,
  services,
}: FieldProps<T>) => {
  const { openModal } = useModal()

  function modalBody({ onClose }: { onClose: () => void }) {
    return <>
      <QRScanner onScan={(data) => {
          onClose()
          form.setValue(fieldName as any, data as any)
      }} services={services}></QRScanner>
    </>
  }

  return <div className='code-text-fild'>
    <input
      type='text'
      id={fieldName}
      readOnly={readOnly}
      placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
      {...form.register(fieldName as Path<T>, {})} />
    <div className='toolbox'>
      <div onClick={() => openModal((props) => modalBody(props))} className='icon-button'>
        <i className='svgfont-qrcode' />
      </div>
    </div>
  </div>
}

export const codeTextField = <T extends FieldValues>() => withFieldWrap<T>(codeTextInput<T>)

export const cardInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly,
  services,
}: FieldProps<T>) => {
  const constnfcIdReg = /^[A-F0-9]{8,16}$/i
  const { openModal } = useModal()

  return <div className='nfc-fild'>
    <input
      type='text'
      id={fieldName}
      readOnly={readOnly}
      placeholder={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
      {...form.register(fieldName as Path<T>, {
        // required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
        validate: {
          validFormat: (value) => constnfcIdReg.test(value) || translates.for(`forms.${formName}.errors.fields.${fieldName}.invalid`),
        },
      })} />
    <div className='toolbox'>
      <div onClick={() => openModal((props) => NFCReaderPopup({
        ...props, onRead: (cardId) => {
          form.setValue(fieldName as any, cardId as any)
        }, translates
      }))} className='icon-button'>
        <i className='svgfont-nfc' />
      </div>
    </div>

    {/* <NFCReaderPopup
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      onRead={(cardId) => {
        form.setValue(fieldName as any, cardId as any)
      }}
      translates={translates} /> */}
  </div>
}
export const cardField = <T extends FieldValues>() => withFieldWrap<T>(cardInput<T>)

export const datePickerInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  readOnly,
}: FieldProps<T>) => {
  // @ts-ignore: Unreachable code error
  const ReactDatePicker = DatePicker.default ?? DatePicker;
  /* tslint:enable */

  // Get the current value using form.watch
  const value = form.watch(fieldName as Path<T>);

  // return <>{value}</>
  return <ReactDatePicker
    selected={value ? new Date(value) : null}
    onChange={(date: Date | null) => {
      form.setValue(fieldName as Path<T>, date ? (date.toISOString() as PathValue<T, Path<T>>) : ('' as PathValue<T, Path<T>>))
      form.trigger(fieldName as Path<T>)
    }}
    id={fieldName}
    placeholderText={translates.for(`forms.${formName}.fields.${fieldName}.placeholder`)}
    readOnly={readOnly}
    autoComplete='off'
    dateFormat='yyyy-MM-dd' // Customize the format as needed
  />
};

export const datePickerField = <T extends FieldValues>() => withFieldWrap<T>(datePickerInput<T>)

export const machineTypeIDInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => {
  const [types, setTypes] = useState<MachineType[]>()

  useEffect(() => {
    services.machines.types().then((types) => setTypes(types))
  }, [])

  if (types === undefined) {
    return <div className='spinner'></div>
  }

  return <div className='custom-select'>
    {/* <select
      id={fieldName}
      disabled={readOnly}
      {...form.register(fieldName as Path<T>, {
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      })}>
      <option value='' disabled>-</option>
      {types?.map((machineType) => (
        <option key={machineType.id} value={machineType.id} selected={form.getValues()[fieldName] === machineType.id}>
          
        </option>
      ))}
    </select> */}

    <CustomDropdown
      name={fieldName}
      control={form.control}
      options={types?.map((machineType) => ({
        value: machineType.id,
        label: `${machineType.manufacturer} - ${machineType.category} - ${machineType.model}`
      })) || []}
      rules={{
        // required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      }}
    />
  </div>
}
export const machineTypeIDField = <T extends FieldValues>() => withFieldWrap<T>(machineTypeIDInput<T>)

export const machineFailureTypeInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => <div className='custom-select'>
    {/* <select
      id={fieldName}
      disabled={readOnly}
      {...form.register(fieldName as Path<T>, {
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      })}>
      {services.machines.failureTypes().map((type) => <option value={type} selected={form.getValues()[fieldName] === type}>{translates.for('dicts.machineFailureTypes.' + MachineFailureTypes[type])}</option>)}
    </select> */}

    <CustomDropdown
      name={fieldName}
      control={form.control}
      options={services.machines.failureTypes().map((type) => ({
        value: String(type as number),
        label: translates.for('dicts.machineFailureTypes.' + MachineFailureTypes[type])
      })) || []}
      rules={{
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
      }}
    />
  </div>

export const machineFailureTypeField = <T extends FieldValues>() => withFieldWrap<T>(machineFailureTypeInput<T>)

export const textareaInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly
}: FieldProps<T>) => <textarea
  id={fieldName}
  disabled={readOnly}
  {...form.register(fieldName as Path<T>, {
    required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`),
  })}>
  </textarea>

export const textareaField = <T extends FieldValues>() => withFieldWrap<T>(textareaInput<T>)

// Define types to ensure TypeScript understands the structure
interface SpeechRecognitionAlternative {
  transcript: string
}

interface SpeechRecognitionResult {
  isFinal: boolean
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionEvent {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionResultList {
  length: number
  [index: number]: SpeechRecognitionResult
}

export const speechInput = <T extends FieldValues>({
  fieldName,
  formName,
  form,
  translates,
  services,
  readOnly,
  session
}: FieldProps<T> & {
  session?: CurrentSession
}) => {
  const anyWin = window as any
  const SpeechRecognition = anyWin.SpeechRecognition || anyWin.webkitSpeechRecognition

  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState<string | undefined>(undefined)
  const recognitionRef = useRef<InstanceType<typeof SpeechRecognition> | null>(null)

  const startRecord = () => {
    let finalTranscript
    if (!SpeechRecognition) return

    const recognition = new SpeechRecognition()
    recognitionRef.current = recognition
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = session?.user.lang || services.i18n.defaultLang()

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let intTranscript = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        let result = event.results[i];
        // interimTranscript += event.results[i][0].transcript + ' '
        if (result.isFinal) {
          // ✅ This is the final recognized text
          finalTranscript += result[0].transcript + " ";
        } else {
          // 🔄 This is an interim (live) transcript
          intTranscript += result[0].transcript;
        }
      }
      setTranscript((v) => v + finalTranscript)
      setInterimTranscript(intTranscript)
    }

    recognition.start()
  }

  useEffect(() => {
    form.setValue(fieldName as any, transcript as any);
  }, [transcript, form, fieldName]);

  const handleMicClick = () => {
    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionRef.current = null
      }
    } else {
      startRecord()
    }
    setIsRecording(!isRecording)
  }

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTranscript(event.target.value)
  }

  return <div className='speech-box'>
    {SpeechRecognition ? (
      <div className={`microphone ${isRecording ? 'active' : ''}`} onClick={handleMicClick}>
        <i className='svgfont-microphone' />
      </div>
    ) : null}
    <textarea
      id={fieldName}
      value={interimTranscript ? transcript + interimTranscript : transcript}
      disabled={readOnly}
      {...form.register(fieldName as any, {
        required: translates.for(`forms.${formName}.errors.fields.${fieldName}.required`)
      })}
      onChange={handleTextareaChange}
    />
  </div>
}

export const speechField = <T extends FieldValues>() => withFieldWrap<T>(speechInput<T>)